import React from 'react';
import {
  canEditOpeningBalanceFields,
  formatOpeningBalanceDisplayDate,
  getOpeningBalanceMinDate,
  shouldShowOpeningBalanceDateField,
} from '../../utils/openingBalanceDate';
import OpeningBalanceUnlockButton from '../../utils/OpeningBalanceUnlockButton';

const formatFieldError = (value) => {
  if (value == null || value === '') {
    return '';
  }
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  if (typeof value === 'object') {
    const first = Object.values(value)[0];
    return Array.isArray(first) ? first[0] || '' : first || '';
  }
  return String(value);
};

const MlaCouponModal = ({
  preventNonNumericalInput,
  hide,
  errors = {},
  stat_operation,
  inputValues = {},
  double,
  handleSubmit,
  handleInputs,
  handleOpeningBalanceInput,
  canEditOpeningBalance,
  onUnlockOpeningBalance,
  isOwnerRole,
}) => {
  const canEditOpeningFields = canEditOpeningBalanceFields({
    statOperation: stat_operation,
    openingBalanceUnlocked: canEditOpeningBalance,
  });

  const formatOpeningBalanceDisplay = (amount, debitCredit) => {
    const numericAmount = Number(amount || 0);
    const formattedAmount = numericAmount.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return debitCredit === 'Dr' ? `(${formattedAmount})` : formattedAmount;
  };

  const isEditMode = Boolean(inputValues.id);
  // After ops start, show OB fields only when unlocked (same as other screens).
  const showOpeningBalance =
    inputValues.mla_coupon === 'yes' &&
    (stat_operation !== 'Yes' || canEditOpeningFields);
  const showOpeningBalanceDateField = shouldShowOpeningBalanceDateField({
    statOperation: stat_operation,
    openingBalanceUnlocked: canEditOpeningBalance,
  });

  const openingBalanceHistory = Array.isArray(inputValues.opening_balance_edit_history)
    ? inputValues.opening_balance_edit_history.filter(
        (row) => row && typeof row === 'object'
      )
    : [];

  const onOpeningBalanceChange = handleOpeningBalanceInput || handleInputs;

  return (
    <React.Fragment>
      <div className="modal-overlay" />
      <div className="modal-wrapper" aria-modal aria-hidden tabIndex={-1} role="dialog">
        <div className="modal">
          <div className="modal-header">
            <h5 className="modal-title" id="staticBackdropLabel">
              {isEditMode ? 'Edit MLA Coupon Configuration' : 'MLA Coupon Configuration'}
            </h5>
            <div className="d-flex align-items-center gap-2">
              <OpeningBalanceUnlockButton
              statOperation={stat_operation}
              unlocked={Boolean(canEditOpeningBalance)}
              canUnlock={isOwnerRole}
              onUnlock={onUnlockOpeningBalance}
            />
              <button
                type="button"
                className="modal-close-button"
                data-dismiss="modal"
                aria-label="Close"
                onClick={hide}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div className="row justify-content-center py-4 px-3">
            <label className="col-sm-5 col-form-label mb-3">
              Do you have MLA Coupon? <sup className="star">*</sup>
            </label>
            <div className="col-sm-4 mb-3">
              <div className="select-container highlight">
                <select
                  className="form-control"
                  name="mla_coupon"
                  value={inputValues.mla_coupon || ''}
                  onChange={handleInputs}
                  disabled={isEditMode}
                >
                  <option value="">select</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
              <div className="errorMsg">{formatFieldError(errors.mla_coupon)}</div>
            </div>

            {isEditMode && stat_operation === 'Yes' && !canEditOpeningFields && (
              <div className="col-sm-10 mb-2">
                <p className="text-muted small mb-0">
                  Opening balance is locked. Owner or Manager must click &quot;Unlock Opening Balance Edit&quot; above to change amount or date.
                </p>
              </div>
            )}

            {showOpeningBalance && (
              <>
                <div className="col-sm-10">
                  <label className="form-label">Opening Balance</label>
                  <input
                    type="number"
                    className="form-control mb-2 hide-arrow"
                    onKeyPress={(event) =>
                      preventNonNumericalInput(
                        event,
                        inputValues.opening_balance ?? ''
                      )
                    }
                    name="opening_balance"
                    readOnly={!canEditOpeningFields}
                    value={inputValues.opening_balance ?? ''}
                    onChange={onOpeningBalanceChange}
                    placeholder="Opening Balance"
                  />
                  <div className="errorMsg">
                    {formatFieldError(errors.opening_balance)}
                  </div>
                  <span className="amount-words"></span>
                </div>
                {showOpeningBalanceDateField && (
                <div className="col-sm-10 mb-3">
                  <label className="form-label">Opening Balance Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="ac_date"
                    value={inputValues.ac_date || ''}
                    min={getOpeningBalanceMinDate()}
                    onChange={handleInputs}
                    readOnly={!canEditOpeningFields}
                    disabled={!canEditOpeningFields}
                  />
                </div>
                )}
              </>
            )}

            {openingBalanceHistory.length > 0 && (
              <div className="col-sm-12 mt-3">
                <h6 className="mb-2">Opening Balance Edit History</h6>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead>
                      <tr>
                        <th>Previous Value</th>
                        <th>Previous Date</th>
                        <th>New Value</th>
                        <th>New Date</th>
                        <th>Edited By</th>
                        <th>Edited At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openingBalanceHistory.map((log, idx) => (
                        <tr key={`mla-ob-log-${idx}`}>
                          <td>
                            {formatOpeningBalanceDisplay(
                              log.previous_opening_balance,
                              log.previous_debit_credit
                            )}
                          </td>
                          <td>
                            {formatOpeningBalanceDisplayDate(log.previous_ac_date) || '—'}
                          </td>
                          <td>
                            {formatOpeningBalanceDisplay(
                              log.new_opening_balance,
                              log.new_debit_credit
                            )}
                          </td>
                          <td>
                            {formatOpeningBalanceDisplayDate(log.new_ac_date) || '—'}
                          </td>
                          <td>{log.edited_by || 'Unknown'}</td>
                          <td>
                            {log.edited_at
                              ? new Date(log.edited_at).toLocaleString('en-IN')
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="modal-footer text-center">
              {double === true ? (
                <button
                  type="button"
                  className="btn btn-primary w-auto"
                  onClick={handleSubmit}
                >
                  Save MLA Coupon Configuration
                </button>
              ) : (
                <button type="button" className="btn btn-primary w-auto" disabled>
                  Save MLA Coupon Configuration
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default MlaCouponModal;
